import { useEffect, useState } from "react";
import { Loader2, Coins, TrendingUp, BellRing, Save } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const usd = (v, d = 2) => (v == null ? "—" : `$${Number(v).toFixed(d)}`);
const METHOD = { credit: "Credit", subscription: "Unlimited", admin: "Admin", preview: "Free sample", free: "Free" };

const Tile = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-gold-500/12 bg-navy-900/40 p-5" data-testid={testid}>
    <div className="font-ui text-[11px] uppercase tracking-widest text-slate-400 mb-2">{label}</div>
    <div className="font-display text-3xl font-bold text-slate-100">{value}</div>
  </div>
);

const ModeTable = ({ rows }) => (
  <div className="rounded-2xl border border-gold-500/12 bg-navy-900/40 p-5 overflow-x-auto" data-testid="ai-costs-by-mode">
    <h3 className="font-ui text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Cost, revenue &amp; margin by book type</h3>
    <table className="w-full font-ui text-sm">
      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
        <th className="py-2">Type</th><th>Books</th><th>Avg AI cost</th><th>Avg revenue</th><th>Total cost</th><th>Total revenue</th><th>Margin</th>
      </tr></thead>
      <tbody className="divide-y divide-gold-500/10">
        {rows.map((r) => (
          <tr key={r.mode} data-testid={`ai-costs-mode-${r.mode}`} className="text-slate-200">
            <td className="py-2.5">{r.label}</td><td>{r.books}<span className="text-slate-500 text-xs"> ({r.paid_books} paid)</span></td>
            <td>{usd(r.avg_cost, 3)}</td><td>{usd(r.avg_revenue)}</td><td>{usd(r.cost)}</td><td>{usd(r.revenue)}</td>
            <td className={r.margin >= 0 ? "text-emerald-300" : "text-rose-300"}>{usd(r.margin)}{r.margin_pct != null && <span className="text-xs text-slate-500"> ({r.margin_pct}%)</span>}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={7} className="py-4 text-slate-500 font-read">No tracked books yet: costs appear as new books are generated.</td></tr>}
      </tbody>
    </table>
  </div>
);

const TopBooks = ({ rows }) => (
  <div className="rounded-2xl border border-gold-500/12 bg-navy-900/40 p-5 overflow-x-auto" data-testid="ai-costs-top-books">
    <h3 className="font-ui text-sm font-semibold text-slate-200 mb-3">Most expensive recent books</h3>
    <table className="w-full font-ui text-sm">
      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
        <th className="py-2">Book</th><th>Type</th><th>Paid with</th><th>AI calls</th><th>Images</th><th>Cost</th><th>Revenue</th><th>Margin</th>
      </tr></thead>
      <tbody className="divide-y divide-gold-500/10">
        {rows.map((r) => (
          <tr key={r.id} className="text-slate-200" data-testid={`ai-costs-book-${r.id}`}>
            <td className="py-2.5 max-w-[240px] truncate">{r.title}{r.refunded && <span className="ml-2 text-[10px] text-rose-300">refunded</span>}</td>
            <td>{r.mode}</td><td>{METHOD[r.method] || r.method}</td><td>{r.calls}</td><td>{r.images}</td>
            <td>{usd(r.cost, 3)}</td><td>{usd(r.revenue)}</td>
            <td className={r.margin >= 0 ? "text-emerald-300" : "text-rose-300"}>{usd(r.margin)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AlertSettings = ({ initial, onSaved }) => {
  const [limit, setLimit] = useState(initial.daily_limit || "");
  const [email, setEmail] = useState(initial.alert_email || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.adminSaveAiCostSettings({ daily_limit: Number(limit) || 0, alert_email: email }); toast.success("Alert saved"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const cls = "bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2 font-ui text-sm text-slate-100 outline-none focus:border-gold-400/60";
  return (
    <div className="rounded-2xl border border-gold-500/12 bg-navy-900/40 p-5" data-testid="ai-costs-alert">
      <h3 className="font-ui text-sm font-semibold text-slate-200 mb-1 flex items-center gap-2"><BellRing className="w-4 h-4" /> Daily spend alert</h3>
      <p className="font-read text-xs text-slate-500 mb-3">We email you once per day if AI spend passes this amount. Set 0 to turn it off.</p>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-ui text-sm text-slate-400">$</span>
        <input data-testid="ai-costs-limit-input" type="number" min="0" step="0.5" value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="e.g. 10" className={`${cls} w-28`} />
        <input data-testid="ai-costs-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (defaults to admin)" className={`${cls} w-64`} />
        <button data-testid="ai-costs-save-alert" onClick={save} disabled={saving}
          className="flex items-center gap-2 font-ui text-sm font-medium bg-gold-400 hover:bg-gold-300 text-stone-950 rounded-lg px-4 py-2 transition-colors disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>
      {initial.last_alert_day && <p className="font-ui text-xs text-amber-300 mt-2">Last alert sent: {initial.last_alert_day}</p>}
    </div>
  );
};

export const AiCosts = () => {
  const [d, setD] = useState(null);
  const load = () => api.adminAiCosts().then(setD).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="min-h-[30vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-slate-400 animate-spin" /></div>;
  return (
    <div className="space-y-6" data-testid="admin-ai-costs">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Tile label="AI spend today" value={usd(d.totals.today)} testid="ai-costs-today" />
        <Tile label="Last 7 days" value={usd(d.totals.d7)} testid="ai-costs-7d" />
        <Tile label="Last 30 days" value={usd(d.totals.d30)} testid="ai-costs-30d" />
      </div>
      <ModeTable rows={d.by_mode} />
      <TopBooks rows={d.top_books} />
      <AlertSettings initial={d.settings} onSaved={load} />
      <p className="font-read text-xs text-slate-500 flex items-start gap-2" data-testid="ai-costs-note">
        <Coins className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        Costs use each call's real token and image counts at the providers' published prices; your Universal Key billing may differ slightly.
        Revenue: credits at your average paid price per credit ({usd(d.credit_price)}); Unlimited plans are split across that subscriber's books; free samples and admin books count as $0. Tracking started with this release.
      </p>
    </div>
  );
};
